import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { ContactMember } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";
import { EditContactMemberModal } from "@/modals/contacts/edit-contact-member-modal";
import { useHiddenContactTabs } from "@/hooks/use-contact-tabs";
import { contactTabPath } from "@/sites/contacts/contact/tabs";
import { useProjectContactsContext } from "./context";

export function RowActions({ cell }: CellContext<ContactMember, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();
	const { projectId } = useProjectContactsContext();

	/** Hooks */
	const { t } = useTranslation();
	const editContactMemberModal = useDisclosure();
	const editProjectOverrideModal = useDisclosure();
	const contactMemberId = cell.row.original.id;
	const contactId = cell.row.original._contact.id;
	const navigate = useNavigate();
	const asyncModal = useAsyncModal();
	const resetAsyncModal = useAsyncModal();
	const hiddenContactTabs = useHiddenContactTabs();

	/** Query */
	const { citadel } = useCitadel();
	const { project } = useProject({ projectId });

	/** Functions */
	async function handleOnCompleteEdit() {
		await refetch?.();
	}

	function handleOnNavigateToContact() {
		if (!contactId) return;
		navigate(contactMembersPath);
	}

	async function handleOnResetMemberOverride() {
		try {
			const isConfirmed = await resetAsyncModal.confirm();
			if (!isConfirmed) return;
			const response = await client.projects[":projectId"].contacts[":contactId"].members[
				":contactMemberId"
			].override.$delete({
				param: { projectId, contactId, contactMemberId },
			});
			await responseHandler(response);
			await refetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnRemoveMemberFromProject() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.projects[":projectId"].contacts[":contactId"].members[
				":contactMemberId"
			].$delete({
				param: { projectId, contactId, contactMemberId },
			});
			await refetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	/** Computed */
	// Removing the person from the site and its two override entries all write a
	// `/projects/:projectId/...` route, so what governs them is changing the project, bound against
	// this site because `update Project` also reaches only the sites its holder runs; editing the
	// person writes the address book instead. Same three-state guard as `Can` documents.
	const projectWriteIsDenied =
		!!citadel && !!project && citadel.cannot("update", "Project", { ...project });
	const memberWriteIsDenied = !!citadel && citadel.cannot("update", "ContactMembers");

	const contactMembersPath = contactTabPath(contactId, "members", hiddenContactTabs);

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={contactMembersPath}>
				<Menu
					items={createStandardMenuItems({
						onOpen: handleOnNavigateToContact,
						onEdit: memberWriteIsDenied ? undefined : editContactMemberModal.onOpen,
						onDelete: projectWriteIsDenied ? undefined : handleOnRemoveMemberFromProject,
						items: projectWriteIsDenied
							? []
							: [
									{
										label: t("projects.edit_member_project_override"),
										icon: icon("Pencil"),
										onClick: editProjectOverrideModal.onOpen,
									},
									{
										label: t("projects.reset_member_project_override"),
										icon: icon("RotateCcw"),
										onClick: handleOnResetMemberOverride,
									},
								],
					})}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("projects.remove_member_from_project_confirm_message")}
					/>
				)}
				{resetAsyncModal.isOpen && (
					<ConfirmModal
						controller={resetAsyncModal}
						message={t("projects.reset_member_project_override_confirmation")}
					/>
				)}
				{editContactMemberModal.isOpen && (
					<EditContactMemberModal
						isOpen={editContactMemberModal.isOpen}
						onClose={editContactMemberModal.onClose}
						contactId={contactId}
						contactMemberId={contactMemberId}
						onComplete={handleOnCompleteEdit}
					/>
				)}
				{editProjectOverrideModal.isOpen && (
					<EditContactMemberModal
						isOpen={editProjectOverrideModal.isOpen}
						onClose={editProjectOverrideModal.onClose}
						contactId={contactId}
						contactMemberId={contactMemberId}
						onComplete={handleOnCompleteEdit}
						projectOverride={{ projectId, contactId }}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
